import React, { useMemo, useState } from 'react'
import { Accordion, Label, Icon, Loader, Message } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { ReactTable } from '../common'
import { StatusPill, MiniSectionHeader } from '../common/DashboardWidgets'
import { getTeamDetail } from './managerDashboardService'
import { ASSET_STATUS, MEMBER_STATUS, MOVE_STATUS, READINESS_STATUS, ROADBLOCK_STATUS, STATUS_COLORS, getAssetStatusColor } from '../../constants'
import './TeamPanel.css'

// ─── Color helpers ────────────────────────────────────────────────────────────

const readinessColor = status => {
  if (!status) return 'grey'
  return status === READINESS_STATUS.READY ? 'green' : 'red'
}

const roadblockStatusColor = status => {
  if (status === ROADBLOCK_STATUS.OPEN) return 'yellow'
  if (status === ROADBLOCK_STATUS.RESOLVED) return 'green'
  return 'grey'
}

const scheduledMoveStatusColor = status => {
  if (status === MOVE_STATUS.PENDING) return 'blue'
  if (status === MOVE_STATUS.COMPLETE || status === MOVE_STATUS.COMPLETED) return 'green'
  return 'grey'
}

const memberStatusStyle = status => {
  const s = (status || '').toLowerCase()
  if (s === MEMBER_STATUS.ACTIVE)   return STATUS_COLORS.GREEN
  if (s === MEMBER_STATUS.SICK)     return STATUS_COLORS.RED
  if (s === MEMBER_STATUS.TRAINING) return STATUS_COLORS.YELLOW
  return STATUS_COLORS.GREY
}

// ─── TableSection ─────────────────────────────────────────────────────────────

const TableSection = ({ data, columns }) => (
  <div className="tp-table-wrapper">
    <ReactTable data={data} columns={columns} hideGlobalFilter />
  </div>
)

// ─── TeamPanel ────────────────────────────────────────────────────────────────

export const TeamPanel = ({ team }) => {
  const [expanded, setExpanded] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)

  // ── Summary (from list endpoint — always available) ────────────────────────

  const summary = team.summary ?? {}
  const memberSummary = summary.members ?? {}
  const assetSummary = summary.assets ?? {}
  const hasAlerts = (summary.openProblems > 0) || (summary.openRoadblocks > 0)
  const readinessStatus = team.latestReadiness?.status ?? null

  const handleExpand = () => {
    const next = !expanded
    setExpanded(next)
    if (next && !detail && !detailLoading) {
      setDetailLoading(true)
      setDetailError(null)
      getTeamDetail(team.id)
        .then(data => {
          setDetail(data)
          setDetailLoading(false)
        })
        .catch(err => {
          setDetailError(err.message || 'Failed to load team details')
          setDetailLoading(false)
        })
    }
  }

  // ── Detail stats (computed from fetched detail) ────────────────────────────

  const memberStats = useMemo(() => {
    const members = detail?.members ?? []
    const byStatus = {}
    members.forEach(m => {
      const s = (m.status || 'unknown').toLowerCase()
      byStatus[s] = (byStatus[s] || 0) + 1
    })
    return {
      total: members.length,
      byStatus,
      readyCount: members.filter(m => m.latestReadiness?.status === READINESS_STATUS.READY).length,
    }
  }, [detail])

  const assetStats = useMemo(() => {
    const assets = detail?.assets ?? []
    // Backend only returns unresolved problems
    const problems = assets.flatMap(a => a.problems ?? [])
    return {
      total: assets.length,
      functional:   assets.filter(a => a.status === ASSET_STATUS.FUNCTIONAL).length,
      maintenance:  assets.filter(a => a.status === ASSET_STATUS.MAINTENANCE).length,
      outOfService: assets.filter(a => a.status === ASSET_STATUS.OUT_OF_SERVICE).length,
      noStatus:     assets.filter(a => !a.status).length,
      unresolvedProblems: problems.length,
      redTags: problems.filter(p => p.redTag).length,
      readyCount: assets.filter(a => a.latestReadiness?.status === READINESS_STATUS.READY).length,
    }
  }, [detail])

  const roadblockStats = useMemo(() => {
    const roadblocks = detail?.roadblocks ?? []
    return {
      total: roadblocks.length,
      open: roadblocks.filter(r => r.status === ROADBLOCK_STATUS.OPEN).length,
      productionImpacting: roadblocks.filter(r => r.status === ROADBLOCK_STATUS.OPEN && r.productionImpact).length,
      resolved: roadblocks.filter(r => r.status !== ROADBLOCK_STATUS.OPEN).length,
    }
  }, [detail])

  const moveStats = useMemo(() => {
    const moves = detail?.scheduledMoves ?? []
    return {
      total: moves.length,
      pending:  moves.filter(m => m.status === MOVE_STATUS.PENDING).length,
      complete: moves.filter(m => m.status === MOVE_STATUS.COMPLETE || m.status === MOVE_STATUS.COMPLETED).length,
    }
  }, [detail])

  // ── Column definitions ─────────────────────────────────────────────────────

  const memberColumns = useMemo(() => [
    {
      Header: 'Name', accessor: 'name',
      Cell: ({ value, row }) => <Link to={`/members/${row.original.userId}`}>{value}</Link>,
    },
    { Header: 'Role', accessor: 'role' },
    { Header: 'Job Title', accessor: 'jobTitle' },
    { Header: 'Position', accessor: 'position' },
    {
      Header: 'Status', accessor: 'status', id: 'member_status',
      Cell: ({ value }) => {
        const st = memberStatusStyle(value)
        return (
          <span className="tp-member-status" style={{ background: st.bg, color: st.color }}>
            <span className="tp-member-status-dot" style={{ background: st.dot }} />
            {value ?? '—'}
          </span>
        )
      },
    },
    {
      Header: 'Readiness', accessor: 'latestReadiness', id: 'member_readiness',
      Cell: ({ value }) => {
        const status = value?.status ?? null
        return <Label color={readinessColor(status)} size="small">{status ?? 'N/A'}</Label>
      },
    },
  ], [])

  const assetColumns = useMemo(() => [
    {
      Header: 'ICC Name', accessor: 'iccName',
      Cell: ({ value, row }) =>
        row.original.id
          ? <Link to={`/assets/${row.original.id}/view`}>{value}</Link>
          : value,
    },
    { Header: 'Make', accessor: 'make' },
    { Header: 'Model', accessor: 'model' },
    { Header: 'VIN', accessor: 'vin' },
    {
      Header: 'Sub-Type', accessor: 'deviceSubType', id: 'device_sub_type_name',
      Cell: ({ value }) => value?.name ?? '',
    },
    {
      Header: 'Associate', accessor: 'associate', id: 'associate_name',
      Cell: ({ value }) =>
        value?.userId
          ? <Link to={`/members/${value.userId}`}>{value.name}</Link>
          : (value?.name ?? ''),
    },
    {
      Header: 'Status', accessor: 'status', id: 'asset_status',
      Cell: ({ value }) => <Label color={getAssetStatusColor(value)} size="small">{value ?? 'N/A'}</Label>,
    },
    {
      Header: 'Readiness', accessor: 'latestReadiness', id: 'asset_readiness',
      Cell: ({ value }) => {
        const status = value?.status ?? null
        return <Label color={readinessColor(status)} size="small">{status ?? 'N/A'}</Label>
      },
    },
    {
      Header: 'Problems', accessor: 'problems', id: 'asset_problems',
      Cell: ({ value, row }) => {
        const unresolved = (value ?? []).filter(p => !p.resolved)
        const hasRedTag = unresolved.some(p => p.redTag)
        if (unresolved.length === 0) return <span className="tp-dim">—</span>
        return (
          <span>
            <Link to={`/assets/${row.original.id}/problems/${unresolved[0].id}`}>
              <Label size="small">{unresolved.length} open</Label>
            </Link>
            {hasRedTag && <Label color="red" size="small" style={{ marginLeft: '0.3rem' }}>Red Tag</Label>}
          </span>
        )
      },
    },
  ], [])

  const roadblockColumns = useMemo(() => [
    { Header: 'Roadblock Type', accessor: 'roadblockType' },
    { Header: 'DFN', accessor: 'dfn' },
    {
      Header: 'Status', accessor: 'status', id: 'roadblock_status',
      Cell: ({ value }) => <Label color={roadblockStatusColor(value)} size="small">{value}</Label>,
    },
    {
      Header: 'Prod. Impact', accessor: 'productionImpact',
      Cell: ({ value }) => value
        ? <Label color="orange" size="small">Yes</Label>
        : <span className="tp-dim">No</span>,
    },
    { Header: 'Description', accessor: 'description' },
    { Header: 'Date', accessor: 'date' },
    {
      Header: 'Submitted By', accessor: 'submittedBy', id: 'submitted_by_name',
      Cell: ({ value }) => value?.name ?? '',
    },
  ], [])

  const scheduledMoveColumns = useMemo(() => [
    {
      Header: 'Asset', accessor: 'asset', id: 'move_asset_name',
      Cell: ({ value }) =>
        value?.id
          ? <Link to={`/assets/${value.id}/moves`}>{value.iccName}</Link>
          : (value?.iccName ?? ''),
    },
    { Header: 'Move Date', accessor: 'moveDate' },
    {
      Header: 'Status', accessor: 'status', id: 'move_status',
      Cell: ({ value }) => <Label color={scheduledMoveStatusColor(value)} size="small">{value}</Label>,
    },
    {
      Header: 'Target Team', accessor: 'targetTeam', id: 'target_team_name',
      Cell: ({ value }) => value?.name ?? '',
    },
    {
      Header: 'Previous Team', accessor: 'previousTeam', id: 'previous_team_name',
      Cell: ({ value }) => value?.name ?? '',
    },
    { Header: 'Notes', accessor: 'notes' },
  ], [])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`tp-panel${hasAlerts ? ' tp-panel--alert' : ''}`}>

      {/* ── Clickable header ──────────────────────────────────────────────── */}
      <div
        role="button"
        data-testid={`team-panel-${team.id}`}
        onClick={handleExpand}
        className={`tp-header${hasAlerts ? ' tp-header--alert' : ''}${expanded ? ' tp-header--expanded' : ''}`}
      >
        <Icon name={expanded ? 'chevron down' : 'chevron right'} style={{ color: '#888', flexShrink: 0 }} />
        <strong className="tp-header__name">{team.name}</strong>
        <Label color={team.isActive ? 'green' : 'grey'} size="mini">
          {team.isActive ? 'Active' : 'Inactive'}
        </Label>
        {team.market && (
          <Label color="blue" size="mini">{team.market.replace(/_/g, ' ')}</Label>
        )}
        {team.teamType?.name && <Label size="mini">{team.teamType.name}</Label>}
        <Label color={readinessColor(readinessStatus)} size="mini">
          {readinessStatus ? `Readiness: ${readinessStatus}` : 'No Readiness'}
        </Label>

        <div className="tp-header__spacer" />

        {/* Quick stats — always visible, from team summary */}
        <div className="tp-header__quickstats">
          <span className="tp-header__stat">
            <Icon name="user" size="small" style={{ margin: 0 }} /> {memberSummary.total ?? 0}
            {(memberSummary.sick ?? 0) > 0 && (
              <Label color="red" size="mini" style={{ marginLeft: '0.3rem' }}>
                {memberSummary.sick} sick
              </Label>
            )}
          </span>
          <span className="tp-header__stat">
            <Icon name="truck" size="small" style={{ margin: 0 }} /> {assetSummary.total ?? 0}
          </span>
          {summary.openProblems > 0 && (
            <Label color="orange" size="mini">{summary.openProblems} open problems</Label>
          )}
          {summary.openRoadblocks > 0 && (
            <Label color="yellow" size="mini">
              <Icon name="warning sign" style={{ margin: '0 0.25rem 0 0' }} />
              {summary.openRoadblocks} roadblock{summary.openRoadblocks !== 1 ? 's' : ''}
            </Label>
          )}
          {summary.pendingMoves > 0 && (
            <Label color="blue" size="mini">
              {summary.pendingMoves} pending move{summary.pendingMoves !== 1 ? 's' : ''}
            </Label>
          )}
        </div>
      </div>

      {/* ── Expanded content ──────────────────────────────────────────────── */}
      {expanded && (
        <div>
          {detailLoading && (
            <div style={{ padding: '1.5rem', textAlign: 'center' }}>
              <Loader active inline size="small" />
            </div>
          )}
          {detailError && (
            <Message negative size="small" style={{ margin: '0.5rem 1rem' }}>
              <p>{detailError}</p>
            </Message>
          )}
          {detail && <>
          {/* Visual stats breakdown bar */}
          <div className="tp-stats-bar">

            {/* Members */}
            <div className="tp-stats-cell">
              <MiniSectionHeader title="Members" count={memberStats.total} />
              <div className="tp-pills-row">
                {Object.entries(memberStats.byStatus).sort().map(([status, count]) => {
                  const st = memberStatusStyle(status)
                  return <StatusPill key={status} count={count} label={status} {...st} />
                })}
              </div>
              {memberStats.total > 0 && (
                <div className="tp-ready-count">
                  {memberStats.readyCount}/{memberStats.total} ready
                </div>
              )}
            </div>

            {/* Assets */}
            <div className="tp-stats-cell">
              <MiniSectionHeader title="Assets" count={assetStats.total} alert={assetStats.unresolvedProblems > 0} />
              <div className="tp-pills-row">
                {assetStats.functional > 0 && (
                  <StatusPill count={assetStats.functional} label="functional" {...STATUS_COLORS.GREEN} />
                )}
                {assetStats.maintenance > 0 && (
                  <StatusPill count={assetStats.maintenance} label="maintenance" {...STATUS_COLORS.YELLOW} />
                )}
                {assetStats.outOfService > 0 && (
                  <StatusPill count={assetStats.outOfService} label="out of service" {...STATUS_COLORS.RED} />
                )}
                {assetStats.noStatus > 0 && (
                  <StatusPill count={assetStats.noStatus} label="no status" {...STATUS_COLORS.GREY} />
                )}
              </div>
              {assetStats.unresolvedProblems > 0 && (
                <div className="tp-pills-row tp-pills-row--last">
                  <StatusPill count={assetStats.unresolvedProblems} label="open problems" bg={STATUS_COLORS.ORANGE.bg} color={STATUS_COLORS.ORANGE.color} />
                  {assetStats.redTags > 0 && (
                    <StatusPill count={assetStats.redTags} label="red tags" bg={STATUS_COLORS.RED.bg} color={STATUS_COLORS.RED.color} />
                  )}
                </div>
              )}
            </div>

            {/* Roadblocks */}
            <div className="tp-stats-cell">
              <MiniSectionHeader title="Roadblocks" count={roadblockStats.total} alert={roadblockStats.productionImpacting > 0} />
              {roadblockStats.total === 0
                ? <span className="tp-empty">None</span>
                : (
                  <div className="tp-pills-row tp-pills-row--last">
                    {roadblockStats.open > 0 && (
                      <StatusPill count={roadblockStats.open} label="open" bg={STATUS_COLORS.YELLOW.bg} color={STATUS_COLORS.YELLOW.color} />
                    )}
                    {roadblockStats.productionImpacting > 0 && (
                      <StatusPill count={roadblockStats.productionImpacting} label="prod. impact" bg={STATUS_COLORS.RED.bg} color={STATUS_COLORS.RED.color} />
                    )}
                    {roadblockStats.resolved > 0 && (
                      <StatusPill count={roadblockStats.resolved} label="resolved" bg={STATUS_COLORS.GREEN.bg} color={STATUS_COLORS.GREEN.color} />
                    )}
                  </div>
                )}
            </div>

            {/* Scheduled Moves */}
            <div className="tp-stats-cell">
              <MiniSectionHeader title="Scheduled Moves" count={moveStats.total} />
              {moveStats.total === 0
                ? <span className="tp-empty">None</span>
                : (
                  <div className="tp-pills-row tp-pills-row--last">
                    {moveStats.pending > 0 && (
                      <StatusPill count={moveStats.pending} label="pending" bg={STATUS_COLORS.BLUE.bg} color={STATUS_COLORS.BLUE.color} />
                    )}
                    {moveStats.complete > 0 && (
                      <StatusPill count={moveStats.complete} label="complete" bg={STATUS_COLORS.GREEN.bg} color={STATUS_COLORS.GREEN.color} />
                    )}
                  </div>
                )}
            </div>
          </div>

          {/* Detail tables */}
          <div className="tp-tables">
            <Accordion
              defaultActiveIndex={[0, 1, 2, 3]}
              exclusive={false}
              fluid
              styled
              panels={[
                {
                  key: 'members',
                  title: { content: `Members (${memberStats.total})` },
                  content: { content: memberStats.total > 0 ? <TableSection data={detail.members ?? []} columns={memberColumns} /> : <span className="tp-dim">—</span> },
                },
                {
                  key: 'assets',
                  title: { content: `Assets (${assetStats.total})${assetStats.redTags > 0 ? ' — ⚠ Red Tag' : ''}` },
                  content: { content: assetStats.total > 0 ? <TableSection data={detail.assets ?? []} columns={assetColumns} /> : <span className="tp-dim">—</span> },
                },
                {
                  key: 'roadblocks',
                  title: { content: `Roadblocks (${roadblockStats.total})` },
                  content: { content: roadblockStats.total > 0 ? <TableSection data={detail.roadblocks ?? []} columns={roadblockColumns} /> : <span className="tp-dim">—</span> },
                },
                {
                  key: 'moves',
                  title: { content: `Scheduled Moves (${moveStats.total})` },
                  content: { content: moveStats.total > 0 ? <TableSection data={detail.scheduledMoves ?? []} columns={scheduledMoveColumns} /> : <span className="tp-dim">—</span> },
                },
              ]}
            />
          </div>
          </>}
        </div>
      )}
    </div>
  )
}